/**
 * Product Page Enhancements
 * Modern JavaScript for product page improvements
 * 
 * Features:
 * - Smooth scroll tracking for scroll depth metrics
 * - Intersection Observer for lazy loading
 * - Performance optimizations
 * 
 * Owner: Theme/Dev
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initScrollDepthTracking();
    initLazyLoading();
    initTrustSignalsAnimation();
    sanitizeDescriptionTables();
  }

  /**
   * Track scroll depth for analytics
   * Reports: 25%, 50%, 75%, 100% scroll depth
   */
  function initScrollDepthTracking() {
    const productPage = document.querySelector('[data-product-enhancements]');
    if (!productPage) return;

    const scrollDepths = [25, 50, 75, 100];
    const trackedDepths = new Set();
    const pageHeight = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);

    function trackScrollDepth() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollPercent = Math.round((scrollTop / pageHeight) * 100);

      scrollDepths.forEach(depth => {
        if (scrollPercent >= depth && !trackedDepths.has(depth)) {
          trackedDepths.add(depth);
          
          // Track in dataLayer if available
          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'scroll_depth',
              scroll_depth: depth,
              page_type: 'product'
            });
          }

          // Track in analytics
          if (typeof gtag !== 'undefined') {
            gtag('event', 'scroll', {
              event_category: 'engagement',
              event_label: depth + '%',
              value: depth
            });
          }
        }
      });
    }

    // Throttle scroll events
    let ticking = false;
    window.addEventListener('scroll', function() {
      if (!ticking) {
        window.requestAnimationFrame(function() {
          trackScrollDepth();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /**
   * Lazy load non-critical content
   */
  function initLazyLoading() {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const target = entry.target;
            
            // Load images
            if (target.dataset.src) {
              target.src = target.dataset.src;
              target.removeAttribute('data-src');
            }
            
            // Load iframes
            if (target.dataset.src && target.tagName === 'IFRAME') {
              target.src = target.dataset.src;
              target.removeAttribute('data-src');
            }
            
            observer.unobserve(target);
          }
        });
      }, {
        rootMargin: '50px'
      });

      // Observe lazy-load elements
      document.querySelectorAll('[data-lazy-load]').forEach(el => {
        observer.observe(el);
      });
    }
  }

  /**
   * Animate trust signals on scroll into view
   */
  function initTrustSignalsAnimation() {
    const trustSection = document.querySelector('[data-trust]');
    if (!trustSection) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll('.kt-trust__item, .kt-delivery-reassurance__item');
            items.forEach((item, index) => {
              setTimeout(() => {
                item.style.opacity = '1';
                item.style.transform = 'translateY(0)';
              }, index * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1
      });

      observer.observe(trustSection);
    }
  }

  /**
   * Strip inline table backgrounds inside product descriptions
   * to prevent dark cells from legacy HTML styling.
   */
  function sanitizeDescriptionTables() {
    const scopes = document.querySelectorAll(
      '.t4s-product__description, .t4s-tab-content, .t4s-product__policies'
    );
    if (!scopes.length) return;

    scopes.forEach(scope => {
      scope.querySelectorAll('table').forEach(table => {
        const elements = [table].concat(Array.from(table.querySelectorAll('*')));
        elements.forEach(el => {
          const rawStyle = el.getAttribute('style') || '';
          const hasInlineBg = el.hasAttribute('bgcolor') || /background(?:-color)?\s*:/i.test(rawStyle);
          const hasInlineColor = /(^|;)\s*color\s*:/i.test(rawStyle);

          if (el.hasAttribute('bgcolor')) {
            el.removeAttribute('bgcolor');
          }

          if (rawStyle) {
            el.style.removeProperty('background');
            el.style.removeProperty('background-color');
            if (hasInlineBg || hasInlineColor) {
              el.style.removeProperty('color');
            }
            const remaining = el.getAttribute('style');
            if (!remaining || remaining.trim() === '') {
              el.removeAttribute('style');
            }
          }
        });
      });
    });
  }

  /**
   * Track add-to-cart interactions
   */
  function trackAddToCart(productId, variantId, quantity) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: 'GBP',
          value: 0, // Will be updated by actual price
          items: [{
            item_id: productId,
            item_variant_id: variantId,
            quantity: quantity
          }]
        }
      });
    }
  }

  // Export for external use
  window.KTProductEnhancements = {
    trackAddToCart: trackAddToCart
  };
})();
